/**
 * 채우기 핸들 시리즈 감지 — Excel 자동 채우기 시맨틱의 순수 구현.
 *
 * 소스 셀 표시값 배열로부터 "step 번째 확장 값"을 돌려주는 생성기를 만든다.
 * step 은 1부터 시작하며, reverse=true 면 소스 앞쪽(위/왼쪽)으로 외삽한다.
 *
 * 감지 우선순위 (Excel 준거):
 * 1. 전부 숫자         — 1개: 복사 / 2개 이상: 최소제곱 선형 추세
 * 2. 월·요일 순환      — en(full/abbr) + ko(1월…, 월…, 월요일…) 테이블 순환
 * 3. 날짜 문자열       — YYYY-MM-DD(구분자 - . /) 일 단위 등차
 * 4. 텍스트+말미 숫자  — 공통 접두면 숫자부 선형 추세(1개면 ±1)
 * 5. 그 외             — 패턴 반복 복사
 */

export type FillGenerator = (step: number) => string;

const MONTHS_EN_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_EN_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_KO = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];
const WEEKDAYS_EN_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEKDAYS_EN_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAYS_KO_FULL = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'];
const WEEKDAYS_KO_ABBR = ['일', '월', '화', '수', '목', '금', '토'];

const CYCLE_TABLES = [
  MONTHS_EN_FULL,
  MONTHS_EN_ABBR,
  MONTHS_KO,
  WEEKDAYS_EN_FULL,
  WEEKDAYS_EN_ABBR,
  WEEKDAYS_KO_FULL,
  WEEKDAYS_KO_ABBR,
];

const formatNumber = (x: number): string => {
  if (Number.isInteger(x)) return String(x);
  return String(parseFloat(x.toPrecision(12)));
};

/** 최소제곱 선형 적합 → 인덱스 i 에서의 예측값 함수 */
const linearFit = (ys: number[]): ((i: number) => number) => {
  const n = ys.length;
  if (n === 1) return () => ys[0];
  const meanX = (n - 1) / 2;
  const meanY = ys.reduce((a, b) => a + b, 0) / n;
  let cov = 0;
  let varX = 0;
  for (let i = 0; i < n; i++) {
    cov += (i - meanX) * (ys[i] - meanY);
    varX += (i - meanX) * (i - meanX);
  }
  const slope = varX === 0 ? 0 : cov / varX;
  const intercept = meanY - slope * meanX;
  return (i: number) => intercept + slope * i;
};

const DATE_RE = /^(\d{4})([-./])(\d{1,2})\2(\d{1,2})$/;

const parseDateParts = (s: string): { y: number; m: number; d: number; sep: string } | null => {
  const m = DATE_RE.exec(s.trim());
  if (!m) return null;
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[3], 10);
  const d = parseInt(m[4], 10);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  return { y, m: mo, d, sep: m[2] };
};

const dateToDays = (p: { y: number; m: number; d: number }): number =>
  Date.UTC(p.y, p.m - 1, p.d) / 86400000;

const daysToDateStr = (days: number, sep: string): string => {
  const dt = new Date(days * 86400000);
  const pad = (v: number) => String(v).padStart(2, '0');
  return `${dt.getUTCFullYear()}${sep}${pad(dt.getUTCMonth() + 1)}${sep}${pad(dt.getUTCDate())}`;
};

const TEXT_NUM_RE = /^(.*?)(\d+)$/;

/** 소스 패턴 반복(순환) 생성기 — forward/backward 모두 패턴 정렬 유지 */
const cycleGenerator = (values: string[], reverse: boolean): FillGenerator => {
  const n = values.length;
  return (step: number) => {
    if (reverse) return values[(((-step) % n) + n) % n];
    return values[(step - 1) % n];
  };
};

export const buildFillGenerator = (values: string[], reverse: boolean): FillGenerator => {
  const n = values.length;
  if (n === 0) return () => '';
  const trimmed = values.map((v) => v.trim());

  // 1) 전부 숫자
  if (trimmed.every((v) => v !== '' && !Number.isNaN(Number(v)))) {
    const nums = trimmed.map(Number);
    if (n === 1) return () => values[0]; // 단일 숫자 드래그 = 복사 (Excel 기본)
    const fit = linearFit(nums);
    return (step: number) => formatNumber(reverse ? fit(-step) : fit(n - 1 + step));
  }

  // 2) 월/요일 순환 테이블
  for (const table of CYCLE_TABLES) {
    const lower = table.map((t) => t.toLowerCase());
    const idxs = trimmed.map((v) => lower.indexOf(v.toLowerCase()));
    if (idxs.every((i) => i >= 0)) {
      const len = table.length;
      let delta = 1;
      if (n >= 2) {
        delta = (((idxs[1] - idxs[0]) % len) + len) % len;
        if (delta === 0) delta = len; // 동일 값 반복 소스 → 한 바퀴 간격
        let consistent = true;
        for (let i = 2; i < n; i++) {
          if ((((idxs[i] - idxs[i - 1]) % len) + len) % len !== delta) {
            consistent = false;
            break;
          }
        }
        if (!consistent) return cycleGenerator(values, reverse);
      }
      return (step: number) => {
        const base = reverse ? idxs[0] - delta * step : idxs[n - 1] + delta * step;
        return table[((base % len) + len) % len];
      };
    }
  }

  // 3) 날짜 문자열 (동일 구분자)
  const dateParts = trimmed.map(parseDateParts);
  if (dateParts.every((p): p is NonNullable<typeof p> => p !== null)) {
    const sep = dateParts[0]!.sep;
    if (dateParts.every((p) => p!.sep === sep)) {
      const days = dateParts.map((p) => dateToDays(p!));
      let delta = 1;
      if (n >= 2) {
        delta = days[1] - days[0];
        const consistent = days.every((d, i) => i === 0 || d - days[i - 1] === delta);
        if (!consistent || delta === 0) return cycleGenerator(values, reverse);
      }
      return (step: number) => {
        const base = reverse ? days[0] - delta * step : days[n - 1] + delta * step;
        return daysToDateStr(base, sep);
      };
    }
  }

  // 4) 텍스트 + 말미 숫자 (공통 접두)
  const textNums = trimmed.map((v) => TEXT_NUM_RE.exec(v));
  if (textNums.every((m): m is RegExpExecArray => m !== null)) {
    const prefix = textNums[0]![1];
    if (textNums.every((m) => m![1] === prefix)) {
      const nums = textNums.map((m) => parseInt(m![2], 10));
      const fit = n === 1 ? (i: number) => nums[0] + (i - 0) : linearFit(nums);
      return (step: number) => {
        const raw = reverse ? fit(-step) : fit(n - 1 + step);
        return `${prefix}${Math.round(raw)}`;
      };
    }
  }

  // 5) 패턴 반복
  return cycleGenerator(values, reverse);
};
